/**
 * Language utilities for multi-language support
 * Requirement 49: Multi-language support for questionnaire
 */
import type { Question, AnswerChoice, SupportedLanguage } from '../types/questionnaire.types';

/**
 * Storage key for language preference
 */
const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

/**
 * Get the current language preference from localStorage
 * Falls back to 'ja' (Japanese) if not set
 */
export const getStoredLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && isValidLanguage(stored)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // localStorage not available
  }
  return 'ja';
};

/**
 * Save language preference to localStorage
 */
export const setStoredLanguage = (lang: SupportedLanguage): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // localStorage not available
  }
};

/**
 * Check if a language code is valid
 */
const isValidLanguage = (lang: string): lang is SupportedLanguage => {
  return ['ja', 'en', 'id', 'mn', 'ne', 'vi', 'ar', 'bn', 'zh', 'es'].includes(lang);
};

/**
 * Get localized text from a Question object
 * Falls back to Japanese (text) if translation is not available
 */
export const getLocalizedQuestionText = (
  question: Question,
  language: SupportedLanguage
): string => {
  if (language === 'ja') {
    return question.text;
  }

  const langMap: Record<Exclude<SupportedLanguage, 'ja'>, keyof Question> = {
    en: 'textEn',
    id: 'textId',
    mn: 'textMn',
    ne: 'textNe',
    vi: 'textVi',
    ar: 'textAr',
    bn: 'textBn',
    zh: 'textZh',
    es: 'textEs',
  };

  const langField = langMap[language];
  const localizedText = question[langField];

  // Return localized text if available, otherwise fall back to Japanese
  return (localizedText as string) || question.text;
};

/**
 * Get localized text from an AnswerChoice object
 * Falls back to Japanese (text) if translation is not available
 */
export const getLocalizedChoiceText = (
  choice: AnswerChoice,
  language: SupportedLanguage
): string => {
  if (language === 'ja') {
    return choice.text;
  }

  const langMap: Record<Exclude<SupportedLanguage, 'ja'>, keyof AnswerChoice> = {
    en: 'textEn',
    id: 'textId',
    mn: 'textMn',
    ne: 'textNe',
    vi: 'textVi',
    ar: 'textAr',
    bn: 'textBn',
    zh: 'textZh',
    es: 'textEs',
  };

  const langField = langMap[language];
  const localizedText = choice[langField];

  // Return localized text if available, otherwise fall back to Japanese
  return (localizedText as string) || choice.text;
};

/**
 * Get all translations for a question (for admin UI)
 */
export const getQuestionTranslations = (
  question: Question
): Record<SupportedLanguage, string> => {
  return {
    ja: question.text,
    en: question.textEn || '',
    id: question.textId || '',
    mn: question.textMn || '',
    ne: question.textNe || '',
    vi: question.textVi || '',
    ar: question.textAr || '',
    bn: question.textBn || '',
    zh: question.textZh || '',
    es: question.textEs || '',
  };
};

/**
 * Get all translations for an answer choice (for admin UI)
 */
export const getChoiceTranslations = (
  choice: AnswerChoice
): Record<SupportedLanguage, string> => {
  return {
    ja: choice.text,
    en: choice.textEn || '',
    id: choice.textId || '',
    mn: choice.textMn || '',
    ne: choice.textNe || '',
    vi: choice.textVi || '',
    ar: choice.textAr || '',
    bn: choice.textBn || '',
    zh: choice.textZh || '',
    es: choice.textEs || '',
  };
};

/**
 * Check if a question has translation for a specific language
 */
export const hasTranslation = (
  question: Question,
  language: SupportedLanguage
): boolean => {
  if (language === 'ja') return true;

  const langMap: Record<Exclude<SupportedLanguage, 'ja'>, keyof Question> = {
    en: 'textEn',
    id: 'textId',
    mn: 'textMn',
    ne: 'textNe',
    vi: 'textVi',
    ar: 'textAr',
    bn: 'textBn',
    zh: 'textZh',
    es: 'textEs',
  };

  const langField = langMap[language];
  return !!(question[langField] as string);
};

/**
 * Get translation status for a question
 */
export const getTranslationStatus = (
  question: Question
): Record<SupportedLanguage, boolean> => {
  return {
    ja: true, // Japanese is always available
    en: !!question.textEn,
    id: !!question.textId,
    mn: !!question.textMn,
    ne: !!question.textNe,
    vi: !!question.textVi,
    ar: !!question.textAr,
    bn: !!question.textBn,
    zh: !!question.textZh,
    es: !!question.textEs,
  };
};
